import { useState, useCallback, useMemo } from 'react'
import { submitTicket as apiSubmitTicket, trackTicket as apiTrackTicket, submitTrackingReply as apiSubmitTrackingReply } from '../api-client'
import type { SubmitTicketData, TicketResult, TrackedTicketResult, WidgetError, UseReqdeskReturn } from './types'
import { useReqdeskContext } from './ReqdeskProvider'
import { useRegistry, useRegistrySnapshot } from './registry-context'

export function useReqdesk(): UseReqdeskReturn {
  const ctx = useReqdeskContext()
  const registry = useRegistry()

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<WidgetError | null>(null)

  const submitTicket = useCallback(async (data: SubmitTicketData): Promise<TicketResult> => {
    setIsLoading(true)
    setError(null)
    try {
      return await apiSubmitTicket(ctx.projectId, data)
    } catch (err) {
      const widgetError = err as WidgetError
      setError(widgetError)
      throw widgetError
    } finally {
      setIsLoading(false)
    }
  }, [ctx.projectId])

  const trackTicket = useCallback(async (token: string): Promise<TrackedTicketResult> => {
    setIsLoading(true)
    setError(null)
    try {
      return await apiTrackTicket(token)
    } catch (err) {
      const widgetError = err as WidgetError
      setError(widgetError)
      throw widgetError
    } finally {
      setIsLoading(false)
    }
  }, [])

  const submitTrackingReply = useCallback(async (token: string, body: string): Promise<void> => {
    setIsLoading(true)
    setError(null)
    try {
      await apiSubmitTrackingReply(token, body)
    } catch (err) {
      const widgetError = err as WidgetError
      setError(widgetError)
      throw widgetError
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Reactive slices. We select narrowly so components only re-render when their slice changes.
  const isOpen = useRegistrySnapshot(s => s.isOpen)
  const currentView = useRegistrySnapshot(s => s.currentView)
  const currentDisplayMode = useRegistrySnapshot(s => s.currentDisplayMode)
  const preferences = useRegistrySnapshot(s => s.preferences)

  // Imperative methods — identities are stable because registry binds once at construction.
  const imperative = useMemo(() => ({
    open: registry.open,
    close: registry.close,
    toggle: registry.toggle,
    openMenu: registry.openMenu,
    openAction: registry.openAction,
    setDisplayMode: registry.setDisplayMode,
    setPreferences: registry.setPreferences,
    addAction: registry.addAction,
    removeAction: registry.removeAction,
    on: registry.on,
  }), [registry])

  return {
    submitTicket,
    trackTicket,
    submitTrackingReply,
    isLoading,
    error,
    isOpen,
    currentView,
    currentDisplayMode,
    preferences,
    ...imperative,
  }
}
